import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// SERVICES
import { getUserADInfo, getUserBDDInfo, insertUser, updateUser } from 'services/userService';
import { upsertUserPreferences, getDefaultPreferences, getUserPreferences } from 'services/tileService';
import { insertConnectionStat, insertUserConnectionStat } from 'services/statService';

// CONTEXT
import { ToastContext } from 'context/ToastContext';
import { AuthContext } from 'context/AuthContext';

export const UserDataContext = createContext({
    userADInfo: null,
    userBDDInfo: null,
    preferences: [],
    loading: true,
    isSuperAdmin: false,
});

// Provider unique : récupère l'identité, les préférences et le statut de l'utilisateur
// connecté UNE SEULE FOIS pour toute l'application. Auparavant la logique vivait dans
// le hook useUserData, instancié dans ~9 composants, ce qui multipliait les appels
// (bdd/info, ad/info, preferences) à chaque montage. Ici, c'est mutualisé.
export function UserDataProvider({ children }) {
    const { addToast } = useContext(ToastContext);
    const { email, isInternal, isExternal, isAdmin, loading: authLoading } = useContext(AuthContext);
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [userADInfo, setUserADInfo] = useState(null);
    const [userBDDInfo, setUserBDDInfo] = useState(null);
    const [preferences, setPreferences] = useState([]);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    const fetchDefaultPreferences = async (user_id) => {
        try {
            const response_default_preferences = await getDefaultPreferences(isExternal ? 'extern' : 'intern');
            let preferencesData = response_default_preferences.data?.[0]?.DEFAULT_PREFERENCES;

            if (!preferencesData) {
                console.error('Erreur lors de la récupération des préférences par défaut :', response_default_preferences);
                addToast(
                    'Préférences par défaut',
                    'Erreur lors de la récupération des préférences par défaut.',
                    null,
                    null,
                    'error'
                );
                return;
            }

            if (typeof preferencesData === 'string') {
                try {
                    preferencesData = JSON.parse(preferencesData);
                } catch (error) {
                    console.error('Erreur lors de la récupération des préférences par défaut :', error);
                    addToast(
                        'Préférences par défaut',
                        'Erreur lors de la récupération des préférences par défaut.',
                        null,
                        null,
                        'error'
                    );
                    return;
                }
            }

            await upsertUserPreferences(user_id, preferencesData, 'default_write');
        } catch (error) {
            addToast(
                'Préférences par défaut',
                'Erreur lors de la récupération des préférences par défaut.',
                null,
                null,
                'error'
            );
            console.error('Erreur lors de la récupération des préférences par défaut :', error);
        }
    };

    const openIRRIS = () => {
        window.open('https://irris.altitudeinfra.fr/#/tickets/create', '_blank');
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response_user = await getUserADInfo(email);
                if (response_user) {
                    setUserADInfo(response_user);

                    const userExists = await getUserBDDInfo(response_user.data.ad_ai.uaiad_mail);

                    if (userExists.data.length === 0) {
                        // Nouvel utilisateur
                        let newUser = null;
                        try {
                            newUser = await insertUser(
                                response_user.data.ad_ai.uaiad_mail,
                                response_user.data.ad_ai.uaiad_physicaldeliveryofficename,
                                isExternal,
                                isAdmin,
                                'Google'
                            );
                            setUserBDDInfo(newUser);
                            setIsSuperAdmin(newUser.data[0].USR_IS_SUPER_ADMIN || false);

                            const userId = newUser.data[0].USR_ID;

                            // Paralléliser les appels non-dépendants
                            const [preferencesResponse] = await Promise.all([
                                getUserPreferences(userId).catch(() => ({ data: [] })),
                                // Statistiques en arrière-plan (ne bloque pas)
                                insertConnectionStat(userId).catch(err =>
                                    console.error('Erreur lors de l\'insertion de la statistique de connexion :', err)
                                )
                            ]);

                            if (preferencesResponse.data.length === 0) {
                                // Pas de préférences, charger les défaut et les sauvegarder
                                await fetchDefaultPreferences(userId);
                                const updatedPreferences = await getUserPreferences(userId);
                                setPreferences(updatedPreferences.data);
                            } else {
                                setPreferences(preferencesResponse.data);
                            }
                        } catch (error) {
                            addToast(
                                'Données utilisateur',
                                'Erreur lors de l\'insertion des données utilisateur.',
                                null,
                                null,
                                'error'
                            );
                            console.error('Erreur lors de l\'insertion des données utilisateur :', error);
                        }
                    } else {
                        // Utilisateur existant
                        setUserBDDInfo(userExists);
                        setIsSuperAdmin(userExists.data[0].USR_IS_SUPER_ADMIN || false);

                        const lastConnection = new Date();
                        lastConnection.setHours(lastConnection.getHours() + 2);
                        const userId = userExists.data[0].USR_ID;

                        // Paralléliser les appels : préférences et update user.
                        // Le log de connexion par route est géré dans un effet dédié
                        // (déclenché par pathname) pour ne pas rejouer toute la chaîne.
                        const [preferencesResponse] = await Promise.all([
                            getUserPreferences(userId).catch(() => null),
                            updateUser(response_user.data.ad_ai.uaiad_mail, {
                                last_connection_at: lastConnection,
                                is_external: isExternal,
                                is_admin: isAdmin,
                            }).catch(err =>
                                console.error('Erreur lors de la mise à jour de l\'utilisateur :', err)
                            )
                        ]);

                        // Un GET en échec (500, timeout, 429...) ne doit jamais être confondu avec
                        // « l'utilisateur n'a pas de préférences » : écrire les défauts détruirait le
                        // layout existant, P_IUD_PREFERENCES_00 supprimant tout ce qui n'est pas
                        // dans le payload.
                        if (preferencesResponse === null) {
                            addToast(
                                'Préférences',
                                'Impossible de charger vos préférences pour le moment. Votre interface est intacte, réessayez dans quelques instants.',
                                () => openIRRIS(),
                                'Ouvrir un ticket',
                                'error'
                            );
                            return;
                        }

                        if (preferencesResponse.data.length === 0) {
                            await fetchDefaultPreferences(userId);
                            const updatedPreferences = await getUserPreferences(userId);
                            setPreferences(updatedPreferences.data);
                        } else {
                            setPreferences(preferencesResponse.data);
                        }
                    }
                } else {
                    throw new Error('Utilisateur introuvable dans Active Directory.');
                }
            } catch (error) {
                addToast(
                    'Données utilisateur',
                    'Erreur lors de la récupération des données utilisateur.',
                    () => openIRRIS(),
                    'Ouvrir un ticket',
                    'error'
                );
                console.error('Erreur lors de la récupération des données utilisateur :', error);
            } finally {
                setLoading(false);
            }
        };

        // Tant que l'auth n'est pas prête, on reste en chargement : évite que les
        // gardes (ProtectedRoute / SuperAdminRoute) ne redirigent prématurément
        // vers /error au F5, avant que l'email ne soit disponible.
        if (authLoading) {
            setLoading(true);
            return;
        }

        if (email && (isInternal || isExternal || isAdmin)) {
            setLoading(true);
            fetchData();
        } else {
            setLoading(false);
        }
    }, [authLoading, email, isInternal, isExternal, isAdmin]);

    // Log de connexion par route : effet léger, déclenché uniquement par le
    // changement de page (et une fois l'identité connue). Ne rejoue pas la
    // chaîne d'identité/préférences à chaque navigation.
    useEffect(() => {
        const userId = userBDDInfo?.data?.[0]?.USR_ID;
        if (!userId) return;

        const at = new Date();
        at.setHours(at.getHours() + 2);
        insertUserConnectionStat(userId, at, location.pathname).catch(err =>
            console.error('Erreur lors de l\'insertion de la statistique de connexion :', err)
        );
    }, [userBDDInfo, location.pathname]);

    // Rafraîchit uniquement les préférences (léger) sans rejouer la chaîne
    // d'identité. À appeler en revenant sur la home après une édition d'interface
    // ou des modifs admin, pour récupérer les tuiles à jour.
    const refreshPreferences = useCallback(async () => {
        const userId = userBDDInfo?.data?.[0]?.USR_ID;
        if (!userId) return;
        try {
            const res = await getUserPreferences(userId);
            setPreferences(res.data);
        } catch (err) {
            console.error('Erreur lors du rafraîchissement des préférences :', err);
        }
    }, [userBDDInfo]);

    const value = useMemo(
        () => ({ userADInfo, userBDDInfo, preferences, loading, isSuperAdmin, refreshPreferences }),
        [userADInfo, userBDDInfo, preferences, loading, isSuperAdmin, refreshPreferences]
    );

    return (
        <UserDataContext.Provider value={value}>
            {children}
        </UserDataContext.Provider>
    );
}
